import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Shield,
  DollarSign,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Building2,
  Calculator,
  Clock,
  Target,
  Activity,
  PieChart,
  BarChart3,
  Download,
  Filter,
} from "lucide-react";
import { securitfeeAPI, operatorAPI, formatCurrency } from "../services/api";
import PDFExporter from "../utils/pdfExporter";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

// Import operator logos
import unitelLogo from "../assets/logos/unitellogo.png";
import ltcLogo from "../assets/logos/ltclogo.png";
import tplusLogo from "../assets/logos/tpluslogo.png";
import etlLogo from "../assets/logos/etllogo.png";
import bestLogo from "../assets/logos/bestlogo.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const SecurityFeeDashboard = ({
  appliedMonth: appliedMonthProp,
  appliedYear: appliedYearProp,
}) => {
  const appliedMonth =
    appliedMonthProp ??
    (new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const appliedYear =
    appliedYearProp ??
    (new Date().getMonth() === 0
      ? new Date().getFullYear() - 1
      : new Date().getFullYear());

  const [operatorData, setOperatorData] = useState([]);
  const [subscriberData, setSubscriberData] = useState([]);
  const [overviewData, setOverviewData] = useState({});
  const [monthlyData, setMonthlyData] = useState({
    subscribers: [],
    revenue: [],
  });
  const [selectedPeriod, setSelectedPeriod] = useState("current");

  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const operatorLogos = useMemo(
    () => ({
      UNITEL: unitelLogo,
      LTC: ltcLogo,
      TPLUS: tplusLogo,
      ETL: etlLogo,
      BEST: bestLogo,
    }),
    [],
  );

  const operatorColors = useMemo(
    () => ({
      UNITEL: "#f59e0b",
      LTC: "#ef4444",
      TPLUS: "#8b5cf6",
      ETL: "#3b82f6",
      BEST: "#1c066bff",
    }),
    [],
  );

  // Month options for filtering
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Fetch when parent's applied month/year or local period changes
  useEffect(() => {
    fetchSecurityFeeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedMonth, appliedYear, selectedPeriod]);

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const selectedMonthName =
        months.find((m) => m.value === appliedMonth)?.label || "Current";
      const filename = `security-fee-dashboard-${selectedMonthName.toLowerCase()}`;

      const result = await PDFExporter.exportToPDF(
        "security-fee-dashboard",
        filename,
        {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        },
      );

      if (result.success) {
        // Success notification could be added here
        console.log("PDF exported successfully");
      } else {
        console.error("PDF export failed:", result.error);
        alert("Failed to export PDF: " + result.error);
      }
    } catch (error) {
      console.error("PDF export error:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchSecurityFeeData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let targetMonth = appliedMonth;
      let targetYear = appliedYear;

      if (selectedPeriod === "previous") {
        targetMonth = appliedMonth - 1;
        if (targetMonth < 1) {
          targetMonth = 12;
          targetYear = appliedYear - 1;
        }
      } else if (selectedPeriod === "quarterly") {
        const quarterIndex = Math.floor((appliedMonth - 1) / 3);
        targetMonth = (quarterIndex + 1) * 3; // last month of quarter
      } else if (selectedPeriod === "yearly") {
        targetMonth = 12; // use December for yearly aggregation
      }

      const params = { month: targetMonth, year: targetYear };

      console.log("Fetching security fee data for:", params);

      const [overviewResponse, operatorResponse, byOperatorResponse] =
        await Promise.all([
          securitfeeAPI.getOverview(params).catch((e) => {
            console.error("Overview API error:", e);
            return null;
          }),
          operatorAPI.getAll().catch((e) => {
            console.error("Operators API error:", e);
            return null;
          }),
          securitfeeAPI.getByOperator(params).catch((e) => {
            console.error("By-Operator API error:", e);
            return null;
          }),
        ]);

      console.log("API Responses:", {
        overviewResponse,
        operatorResponse,
        byOperatorResponse,
      });

      // Process overview data (api returns payload via interceptor as { success, data })
      if (overviewResponse?.success) {
        setOverviewData(overviewResponse.data);
      } else if (
        overviewResponse &&
        !overviewResponse.success &&
        overviewResponse.overview
      ) {
        // Fallback if data is returned without success wrapper (though subagent confirmed wrapper exists)
        setOverviewData(overviewResponse);
      }

      // Generate monthly data for analysis (mock data for demonstration)
      const generateMonthlyData = () => {
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const currentMonth = new Date().getMonth();
        const last12Months = [];

        for (let i = 11; i >= 0; i--) {
          const monthIndex = (currentMonth - i + 12) % 12;
          last12Months.push(months[monthIndex]);
        }

        // Mock subscriber trend data (in millions)
        const subscriberTrend = last12Months.map((month, index) => {
          const baseSubscribers = 9.2;
          const growth = index * 0.1 + (Math.random() * 0.3 - 0.15);
          return Math.max(baseSubscribers + growth, baseSubscribers);
        });

        // Mock revenue data (in billions Kip)
        const revenueTrend = last12Months.map((month, index) => {
          const baseRevenue = 38.5;
          const seasonal = Math.sin((index / 12) * Math.PI * 2) * 2;
          const growth = index * 0.5 + (Math.random() * 2 - 1);
          return Math.max(baseRevenue + seasonal + growth, baseRevenue * 0.8);
        });

        return {
          subscribers: last12Months.map((month, index) => ({
            month,
            value: subscriberTrend[index],
          })),
          revenue: last12Months.map((month, index) => ({
            month,
            value: revenueTrend[index],
          })),
        };
      };

      setMonthlyData(generateMonthlyData());

      // Process operator data with security fee information
      const operators = operatorResponse?.data || operatorResponse || [];
      const operatorFeeData =
        byOperatorResponse?.data || byOperatorResponse || [];

      const processedOperatorData = Array.isArray(operators)
        ? operators.map((operator) => {
            const feeData = Array.isArray(operatorFeeData)
              ? operatorFeeData.find(
                  (op) =>
                    op.operatorName === operator.operator_name ||
                    op.operatorCode === operator.operator_code ||
                    op.operatorCode === operator.operator_name,
                )
              : null;

            const code = (operator.operator_code || "").toUpperCase();
            const name = operator.operator_name || "";

            return {
              id: operator.operator_id,
              name: name,
              code: operator.operator_code || code,
              logo:
                operatorLogos[code] ||
                operatorLogos[name] ||
                operatorLogos[name.toUpperCase()],
              color: operatorColors[code] || operatorColors[name] || "#6b7280",
              expectedAmount: parseFloat(feeData?.expectedAmount || 0),
              collectedAmount: parseFloat(feeData?.collectedAmount || 0),
              pendingAmount: parseFloat(feeData?.pendingAmount || 0),
              overdueAmount: parseFloat(feeData?.overdueAmount || 0),
              collectionRate: parseFloat(feeData?.collectionRate || 0),
              remaining: parseFloat(feeData?.remaining || 0),
              transactionCount: parseInt(feeData?.transactionCount || 0),
              subscribers: parseInt(operator.total_subscribers) || 0,
              activeSubscribers: parseInt(operator.active_subscribers) || 0,
            };
          })
        : [];

      setOperatorData(processedOperatorData);

      // Process subscriber data for the dedicated section
      const processedSubscriberData = Array.isArray(operators)
        ? operators.map((operator) => {
            const code = (operator.operator_code || "").toUpperCase();
            const name = operator.operator_name || "";

            return {
              id: operator.operator_id,
              name: name,
              code: operator.operator_code || code,
              logo:
                operatorLogos[code] ||
                operatorLogos[name] ||
                operatorLogos[name.toUpperCase()],
              color: operatorColors[code] || operatorColors[name] || "#6b7280",
              subscribers: parseInt(operator.total_subscribers) || 0,
              activeSubscribers: parseInt(operator.active_subscribers) || 0,
              compliance: parseFloat(operator.avg_compliance_rate) || 0,
            };
          })
        : [];

      setSubscriberData(processedSubscriberData);
    } catch (err) {
      console.error("Error fetching security fee data:", err);
      setError("Failed to load security fee data: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [
    appliedMonth,
    appliedYear,
    selectedPeriod,
    operatorLogos,
    operatorColors,
  ]);

  const formatKip = formatCurrency;

  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    return new Intl.NumberFormat("en-US").format(parseInt(num));
  };

  const getComplianceStatus = (rate) => {
    if (rate >= 95)
      return {
        status: "excellent",
        color: "green",
        text: "Excellent",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
      };
    if (rate >= 90)
      return {
        status: "good",
        color: "blue",
        text: "Good",
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
      };
    if (rate >= 80)
      return {
        status: "warning",
        color: "yellow",
        text: "Needs Review",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
      };
    return {
      status: "critical",
      color: "red",
      text: "Critical",
      bgColor: "bg-red-100",
      textColor: "text-red-800",
    };
  };

  const calculateTotals = () => {
    return operatorData.reduce(
      (totals, operator) => ({
        totalExpected: totals.totalExpected + operator.expectedAmount,
        totalCollected: totals.totalCollected + operator.collectedAmount,
        totalPending: totals.totalPending + operator.pendingAmount,
        totalOverdue: totals.totalOverdue + operator.overdueAmount,
        totalRemaining: totals.totalRemaining + operator.remaining,
        totalTransactions: totals.totalTransactions + operator.transactionCount,
      }),
      {
        totalExpected: 0,
        totalCollected: 0,
        totalPending: 0,
        totalOverdue: 0,
        totalRemaining: 0,
        totalTransactions: 0,
      },
    );
  };

  const totals = calculateTotals();
  const overallCollectionRate =
    totals.totalExpected > 0
      ? (totals.totalCollected / totals.totalExpected) * 100
      : 0;

  // Chart data
  const collectionRateChartData = {
    labels: operatorData.map((op) => op.code),
    datasets: [
      {
        label: "Collection Rate (%)",
        data: operatorData.map((op) => op.collectionRate),
        backgroundColor: operatorData.map((op) => op.color),
        borderColor: operatorData.map((op) => op.color),
        borderWidth: 1,
      },
    ],
  };

  const collectionAmountChartData = {
    labels: operatorData.map((op) => op.code),
    datasets: [
      {
        label: "Collected",
        data: operatorData.map((op) => op.collectedAmount / 1000000), // Convert to millions
        backgroundColor: "#10b981",
        borderColor: "#10b981",
        borderWidth: 1,
      },
      {
        label: "Remaining",
        data: operatorData.map((op) => op.remaining / 1000000), // Convert to millions
        backgroundColor: "#ef4444",
        borderColor: "#ef4444",
        borderWidth: 1,
      },
    ],
  };

  const overallDistributionData = {
    labels: ["Collected", "Pending", "Remain Collection"],
    datasets: [
      {
        data: [
          totals.totalCollected,
          totals.totalPending,
          totals.totalRemaining,
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#f97316"],
        borderWidth: 2,
        borderColor: "#ffffff",
      },
    ],
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">
            Loading security fee data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  Error Loading Data
                </h3>
                <p className="text-red-600 mt-1">{error}</p>
                <button
                  onClick={fetchSecurityFeeData}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="security-fee-dashboard" className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-4" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Security Fee Dashboard
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                  Comprehensive security fee collection monitoring by operator
                </p>
              </div>
            </div>
            {/* Period Filter (month/year come from parent dashboard Search) */}
            {/* <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm text-gray-600">Period:</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="current">Current Month</option>
                  <option value="previous">Previous Month</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div> */}

            {/* Action Buttons Container */}
            {/* Export Button */}
            {/* <div className="flex items-center gap-2 mt-2 sm:mt-0 w-full sm:w-auto">
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`flex-1 sm:flex-none flex justify-center items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                  isExporting
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                }`}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {isExporting ? "Exporting..." : "Export PDF"}
                </span>
                <span className="sm:hidden">{isExporting ? "..." : "PDF"}</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <Calculator className="h-10 w-10 text-blue-200 mb-2" />
            <p className="text-blue-100 text-sm">Total Expected</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.overview?.totalEstimate || totals.totalExpected,
              )}
            </p>
            <p className="text-blue-100 text-xs mt-2">
              {formatNumber(
                overviewData?.summary?.totalTransactions ||
                  totals.totalTransactions,
              )}{" "}
              transactions
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <CheckCircle className="h-10 w-10 text-green-200 mb-2" />
            <p className="text-green-100 text-sm">Collected</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.overview?.totalCollected || totals.totalCollected,
              )}
            </p>
            <p className="text-green-100 text-xs mt-2">
              {(
                overviewData?.overview?.collectionRate || overallCollectionRate
              ).toFixed(1)}
              % collection rate
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <Clock className="h-10 w-10 text-yellow-200 mb-2" />
            <p className="text-yellow-100 text-sm">Pending</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.breakdown?.pending || totals.totalPending,
              )}
            </p>
            <p className="text-yellow-100 text-xs mt-2">
              {overviewData?.overview?.totalEstimate > 0
                ? (
                    (overviewData.breakdown.pending /
                      overviewData.overview.totalEstimate) *
                    100
                  ).toFixed(1)
                : totals.totalExpected > 0
                  ? (
                      (totals.totalPending / totals.totalExpected) *
                      100
                    ).toFixed(1)
                  : 0}
              % of total
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white text-center">
          <div className="flex flex-col items-center">
            <Target className="h-10 w-10 text-orange-200 mb-2" />
            <p className="text-orange-100 text-sm">Remain Collection</p>
            <p className="text-3xl font-bold mt-1">
              {formatKip(
                overviewData?.overview?.remaining || totals.totalRemaining,
              )}
            </p>
            <p className="text-orange-100 text-xs mt-2">
              {overviewData?.overview?.totalEstimate > 0
                ? (
                    (overviewData.overview.remaining /
                      overviewData.overview.totalEstimate) *
                    100
                  ).toFixed(1)
                : totals.totalExpected > 0
                  ? (
                      (totals.totalRemaining / totals.totalExpected) *
                      100
                    ).toFixed(1)
                  : 0}
              % of total
            </p>
          </div>
        </div>
      </div>

      {/* Monthly Analysis Dashboard */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Activity className="h-6 w-6 mr-2" />
              Monthly Analysis Dashboard
            </h2>
            <div className="text-sm text-gray-600">
              Last 12 Months Trend Analysis
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subscriber Trend Line Chart */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                Subscriber Growth Trend
              </h3>
              <div className="h-80">
                <Line
                  data={{
                    labels: monthlyData.subscribers.map((item) => item.month),
                    datasets: [
                      {
                        label: "Subscribers (Millions)",
                        data: monthlyData.subscribers.map((item) => item.value),
                        borderColor: "#3b82f6",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#3b82f6",
                        pointBorderColor: "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          font: { weight: "bold" },
                          color: "#374151",
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        titleColor: "#ffffff",
                        bodyColor: "#ffffff",
                        callbacks: {
                          label: function (context) {
                            return `Subscribers: ${context.parsed.y.toFixed(2)}M`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { color: "rgba(156, 163, 175, 0.2)" },
                        ticks: { color: "#6b7280", font: { weight: "500" } },
                      },
                      y: {
                        beginAtZero: false,
                        grid: { color: "rgba(156, 163, 175, 0.2)" },
                        ticks: {
                          color: "#6b7280",
                          font: { weight: "500" },
                          callback: function (value) {
                            return value.toFixed(1) + "M";
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                      mode: "index",
                    },
                  }}
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {monthlyData.subscribers.length > 0
                      ? monthlyData.subscribers[
                          monthlyData.subscribers.length - 1
                        ].value.toFixed(2)
                      : "0.00"}
                    M
                  </div>
                  <div className="text-xs text-gray-600">Current Month</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    +
                    {monthlyData.subscribers.length >= 2
                      ? (
                          ((monthlyData.subscribers[
                            monthlyData.subscribers.length - 1
                          ].value -
                            monthlyData.subscribers[0].value) /
                            monthlyData.subscribers[0].value) *
                          100
                        ).toFixed(1)
                      : "0.0"}
                    %
                  </div>
                  <div className="text-xs text-gray-600">12-Month Growth</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-indigo-600">
                    {monthlyData.subscribers.length > 0
                      ? (
                          monthlyData.subscribers.reduce(
                            (sum, item) => sum + item.value,
                            0,
                          ) / monthlyData.subscribers.length
                        ).toFixed(2)
                      : "0.00"}
                    M
                  </div>
                  <div className="text-xs text-gray-600">Average</div>
                </div>
              </div>
            </div>

            {/* Security Fee Revenue Bar Chart */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Security Fee Revenue Trend
              </h3>
              <div className="h-80">
                <Bar
                  data={{
                    labels: monthlyData.revenue.map((item) => item.month),
                    datasets: [
                      {
                        label: "Revenue (Billions ₭)",
                        data: monthlyData.revenue.map((item) => item.value),
                        backgroundColor: monthlyData.revenue.map(
                          (item, index) =>
                            `rgba(16, 185, 129, ${0.6 + index * 0.03})`,
                        ),
                        borderColor: "#10b981",
                        borderWidth: 2,
                        borderRadius: 6,
                        borderSkipped: false,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          font: { weight: "bold" },
                          color: "#374151",
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        titleColor: "#ffffff",
                        bodyColor: "#ffffff",
                        callbacks: {
                          label: function (context) {
                            return `Revenue: ${context.parsed.y.toFixed(2)}B ₭`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { color: "rgba(156, 163, 175, 0.2)" },
                        ticks: { color: "#6b7280", font: { weight: "500" } },
                      },
                      y: {
                        beginAtZero: false,
                        grid: { color: "rgba(156, 163, 175, 0.2)" },
                        ticks: {
                          color: "#6b7280",
                          font: { weight: "500" },
                          callback: function (value) {
                            return value.toFixed(1) + "B ₭";
                          },
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                      mode: "index",
                    },
                  }}
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    {monthlyData.revenue.length > 0
                      ? monthlyData.revenue[
                          monthlyData.revenue.length - 1
                        ].value.toFixed(2)
                      : "0.00"}
                    B ₭
                  </div>
                  <div className="text-xs text-gray-600">Current Month</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    +
                    {monthlyData.revenue.length >= 2
                      ? (
                          ((monthlyData.revenue[monthlyData.revenue.length - 1]
                            .value -
                            monthlyData.revenue[0].value) /
                            monthlyData.revenue[0].value) *
                          100
                        ).toFixed(1)
                      : "0.0"}
                    %
                  </div>
                  <div className="text-xs text-gray-600">12-Month Growth</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">
                    {monthlyData.revenue.length > 0
                      ? (
                          monthlyData.revenue.reduce(
                            (sum, item) => sum + item.value,
                            0,
                          ) / monthlyData.revenue.length
                        ).toFixed(2)
                      : "0.00"}
                    B ₭
                  </div>
                  <div className="text-xs text-gray-600">Average</div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Analysis Summary */}
          <div className="mt-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Performance Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {monthlyData.subscribers.length >= 2
                    ? (
                        (monthlyData.subscribers[
                          monthlyData.subscribers.length - 1
                        ].value -
                          monthlyData.subscribers[
                            monthlyData.subscribers.length - 2
                          ].value) *
                        1000000
                      ).toFixed(0)
                    : "0"}
                </div>
                <div className="text-sm text-blue-800 font-medium">
                  Monthly Subscriber Growth
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {monthlyData.revenue.length >= 2
                    ? (
                        monthlyData.revenue[monthlyData.revenue.length - 1]
                          .value -
                        monthlyData.revenue[monthlyData.revenue.length - 2]
                          .value
                      ).toFixed(2)
                    : "0.00"}
                  B ₭
                </div>
                <div className="text-sm text-green-800 font-medium">
                  Monthly Revenue Change
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">
                  {monthlyData.revenue.length > 0
                    ? (
                        monthlyData.revenue.reduce(
                          (sum, item) => sum + item.value,
                          0,
                        ) * 12
                      ).toFixed(1)
                    : "0.0"}
                  B ₭
                </div>
                <div className="text-sm text-indigo-800 font-medium">
                  Projected Annual Revenue
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {monthlyData.subscribers.length > 0 &&
                  monthlyData.revenue.length > 0
                    ? (
                        (monthlyData.revenue[monthlyData.revenue.length - 1]
                          .value *
                          1000000000) /
                        (monthlyData.subscribers[
                          monthlyData.subscribers.length - 1
                        ].value *
                          1000000)
                      ).toFixed(0)
                    : "0"}{" "}
                  ₭
                </div>
                <div className="text-sm text-purple-800 font-medium">
                  Revenue per Subscriber
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Collection Rate Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Collection Rate by Operator
          </h3>
          <div className="h-64">
            <Bar
              data={collectionRateChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                      callback: function (value) {
                        return value + "%";
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Collection Amount Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Collection Amount (Millions ₭)
          </h3>
          <div className="h-64">
            <Bar
              data={collectionAmountChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </div>

        {/* Overall Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            Overall Distribution
          </h3>
          <div className="h-64">
            <Doughnut
              data={overallDistributionData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "bottom" },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${context.label}: ${formatKip(context.raw)}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Operator Subscribers Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2" />
              Operator Subscribers
            </h2>
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm text-gray-600">Total:</span>
                <span className="ml-2 text-lg font-bold text-gray-900">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.subscribers,
                      0,
                    ),
                  )}
                </span>
              </div>
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">Active Rate:</span>
                <span className="ml-2 text-lg font-bold text-green-600">
                  {subscriberData.length > 0 &&
                  subscriberData.reduce(
                    (total, op) => total + op.subscribers,
                    0,
                  ) > 0
                    ? (
                        (subscriberData.reduce(
                          (total, op) => total + op.activeSubscribers,
                          0,
                        ) /
                          subscriberData.reduce(
                            (total, op) => total + op.subscribers,
                            0,
                          )) *
                        100
                      ).toFixed(1)
                    : "0.0"}
                  %
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {subscriberData.map((operator, index) => {
              const totalSubscribers = subscriberData.reduce(
                (total, op) => total + op.subscribers,
                0,
              );
              const marketShare =
                totalSubscribers > 0
                  ? (operator.subscribers / totalSubscribers) * 100
                  : 0;
              const activeRate =
                operator.subscribers > 0
                  ? (operator.activeSubscribers / operator.subscribers) * 100
                  : 0;

              return (
                <div
                  key={operator.id || operator.name || index}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200 border border-blue-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center p-2 border border-gray-200">
                      {operator.logo ? (
                        <img
                          src={operator.logo}
                          alt={`${operator.name} logo`}
                          className="w-10 h-10 object-contain "
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-10 h-10 rounded-lg ${!operator.logo ? "flex" : "hidden"} items-center justify-center`}
                        style={{ backgroundColor: operator.color }}
                      >
                        <span className="text-white text-sm font-bold ">
                          {operator.name && operator.name.length > 0
                            ? operator.name.charAt(0)
                            : "?"}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      {marketShare.toFixed(1)}%
                    </span>
                  </div>

                  <div className="text-center mb-3">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {operator.name || "Unknown"}
                    </h3>
                    <p
                      className="text-2xl font-bold mb-1"
                      style={{ color: operator.color }}
                    >
                      {formatNumber(operator.subscribers)}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">Subscribers</p>
                    <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      <TrendingUp className="h-3 w-3 inline mr-1" />
                      {activeRate.toFixed(1)}% Active
                    </div>
                  </div>

                  {/* Market share progress bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Market Share</span>
                      <span>{marketShare.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${marketShare}%`,
                          backgroundColor: operator.color,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Active vs Inactive subscribers */}
                  <div className="text-xs text-gray-600">
                    <div className="flex justify-between mb-1">
                      <span>
                        Active: {formatNumber(operator.activeSubscribers)}
                      </span>
                      <span>
                        Inactive:{" "}
                        {formatNumber(
                          operator.subscribers - operator.activeSubscribers,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Statistics */}
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Subscriber Distribution Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.subscribers,
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-blue-800 font-medium">
                  Total Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) => total + op.activeSubscribers,
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-green-800 font-medium">
                  Active Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">
                  {formatNumber(
                    subscriberData.reduce(
                      (total, op) =>
                        total + (op.subscribers - op.activeSubscribers),
                      0,
                    ),
                  )}
                </div>
                <div className="text-sm text-gray-800 font-medium">
                  Inactive Subscribers
                </div>
              </div>
              <div className="text-center p-4 bg-white bg-opacity-70 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">
                  {subscriberData.length}
                </div>
                <div className="text-sm text-indigo-800 font-medium">
                  Total Operators
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Operator Performance Cards */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Building2 className="h-6 w-6 mr-2" />
          Operator Performance Details
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {operatorData.map((operator) => {
            const compliance = getComplianceStatus(operator.collectionRate);

            return (
              <div
                key={operator.id}
                className="bg-white rounded-lg shadow border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {operator.logo && (
                      <img
                        src={operator.logo}
                        alt={operator.name}
                        className="w-12 h-12 object-contain"
                      />
                    )}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {operator.name}
                      </h3>
                      <p className="text-sm text-gray-600">{operator.code}</p>
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${compliance.bgColor} ${compliance.textColor}`}
                  >
                    {compliance.text}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Expected Collection
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatKip(operator.expectedAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatNumber(operator.transactionCount)} transactions
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Amount Collected
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {formatKip(operator.collectedAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.collectionRate.toFixed(1)}% collected
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">Pending</div>
                    <div className="text-lg font-bold text-yellow-600">
                      {formatKip(operator.pendingAmount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.expectedAmount > 0
                        ? (
                            (operator.pendingAmount / operator.expectedAmount) *
                            100
                          ).toFixed(1)
                        : 0}
                      % pending
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Remain Collection
                    </div>
                    <div className="text-lg font-bold text-orange-600">
                      {formatKip(operator.remaining)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {operator.expectedAmount > 0
                        ? (
                            (operator.remaining / operator.expectedAmount) *
                            100
                          ).toFixed(1)
                        : 0}
                      % remaining
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Eligible Subscribers
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {formatNumber(operator.collectedAmount / 3000)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 mb-1">
                      Not Eligible Subscribers
                    </div>
                    <div className="text-lg font-bold text-red-600">
                      {formatNumber(operator.remaining / 3000)}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-700 mb-2">
                    <span>Collection Progress</span>
                    <span>{operator.collectionRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="flex h-3 rounded-full overflow-hidden">
                      <div
                        className="bg-green-500 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.collectedAmount / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                      <div
                        className="bg-yellow-400 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.pendingAmount / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                      <div
                        className="bg-orange-500 transition-all duration-500"
                        style={{
                          width: `${operator.expectedAmount > 0 ? (operator.remaining / operator.expectedAmount) * 100 : 0}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>✓ {formatKip(operator.collectedAmount)}</span>
                    <span>⚠ {formatKip(operator.remaining)}</span>
                  </div>
                </div>

                {/* Action required indicator */}
                {operator.collectionRate < 90 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-800">
                          Action Required
                        </div>
                        <div className="text-xs text-red-700 mt-1">
                          Collection rate is below 90%. Immediate follow-up
                          recommended.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2" />
          Performance Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {operatorData.filter((op) => op.collectionRate >= 95).length}
            </div>
            <div className="text-sm text-gray-600">Operators Above 95%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {
                operatorData.filter(
                  (op) => op.collectionRate >= 90 && op.collectionRate < 95,
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Operators 90-95%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {operatorData.filter((op) => op.collectionRate < 90).length}
            </div>
            <div className="text-sm text-gray-600">Operators Below 90%</div>
          </div>

          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {overallCollectionRate.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Overall Collection Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityFeeDashboard;
